import { useCallback, useEffect, useRef, useState } from 'react';
import { uniqBy } from 'lodash';
import { client, url } from '../../shared';

const emptyList = () => ({
  content: [] as Record<string, any>[],
  nextPage: 0,
  last: false,
  error: false,
});

export default function useDialogLists(revision: any, incomingMessage: any) {
  const [lists, setLists] = useState([emptyList(), emptyList()]);
  const [loading, setLoading] = useState(false);
  const state = useRef(lists);
  const mounted = useRef(false);
  const running = useRef(false);
  const refreshPending = useRef(false);

  const fetchLists = useCallback(async (refresh: boolean) => {
    if (!mounted.current) return;
    if (running.current) {
      // Many socket events during a request cause at most one follow-up.
      if (refresh) refreshPending.current = true;
      return;
    }
    running.current = true;
    setLoading(true);
    try {
      let replace = refresh;
      do {
        refreshPending.current = false;
        await Promise.all(
          [url.chatsActive2, url.chatsProcess2].map(async (endpoint, index) => {
            const previous = state.current[index];
            if (!replace && previous.last) return previous;
            const page = replace ? 0 : previous.nextPage;
            let next = previous;
            try {
              const { data } = await client.post(endpoint, {
                offset: page,
                size: 30,
              });
              const content = data.payload.content;
              const merged = replace
                ? content
                : previous.content.concat(content);
              next = {
                content: uniqBy(merged, 'id') as Record<string, any>[],
                nextPage: page + 1,
                last: data.payload.last === true || content.length < 30,
                error: false,
              };
            } catch {
              // Retain this column and its page cursor; the other can succeed.
              next = { ...previous, error: true };
            }
            if (!mounted.current) return;
            state.current = state.current.map((list, i) =>
              i === index ? next : list
            );
            setLists(state.current);
          })
        );
        if (!mounted.current) return;
        replace = true;
      } while (refreshPending.current);
    } finally {
      running.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      refreshPending.current = false;
    };
  }, []);

  useEffect(() => {
    fetchLists(true);
  }, [revision, incomingMessage, fetchLists]);

  return {
    chats: lists[0].content.concat(lists[1].content),
    activeError: lists[0].error,
    processError: lists[1].error,
    loading,
    refresh: () => fetchLists(true),
    loadMore: () => fetchLists(false),
  };
}
